import { FC, ReactElement, useRef } from 'react'
import classnames from 'classnames'

import { Badge, Button, LoadingSpinner } from '@/components'
import { IconButton } from '@/compositions/@buttons/IconButton/IconButton'
import { Popover, PopoverAlign, PopoverHandle } from '@/compositions/Popover/Popover'
import { NotificationItem } from '@/entities/Notification/Notification'
import { trans } from '@/helpers/trans'

import { NotificationListItem } from './subcomponents'

import './NotificationsPopover.scss'

interface NotificationsPopoverProps {
    notifications: NotificationItem[]
    unreadCount: number
    loading?: boolean
    onMarkAllRead: () => void
    onNotificationClick?: (notification: NotificationItem) => void
    align?: PopoverAlign
    className?: string
}

export const NotificationsPopover: FC<NotificationsPopoverProps> = ({
    notifications,
    unreadCount,
    loading = false,
    onMarkAllRead,
    onNotificationClick,
    align = 'end',
    className = '',
}): ReactElement => {
    const popoverRef = useRef<PopoverHandle>(null)

    const handleNotificationClick = (notification: NotificationItem): void => {
        popoverRef.current?.close()
        onNotificationClick?.(notification)
    }

    return (
        <Popover
            ref={popoverRef}
            label={trans('compositions.notificationsPopover.title')}
            align={align}
            className={`notifications-popover ${className}`}
            contentClassName="notifications-popover__panel"
            renderTrigger={({ id, open }) => (
                <span className="notifications-popover__trigger-wrap">
                    <IconButton
                        hideLabel
                        icon="bell"
                        text={trans('compositions.notificationsPopover.trigger')}
                        popoverTarget={id}
                        popoverTargetAction="toggle"
                        aria-expanded={open}
                        className={classnames('notifications-popover__trigger', {
                            'notifications-popover__trigger--open': open,
                        })}
                        iconClassName="notifications-popover__trigger-icon"
                    />
                    {unreadCount > 0 && (
                        <Badge value={unreadCount} className="notifications-popover__badge" />
                    )}
                </span>
            )}
        >
            <div className="notifications-popover__header">
                <strong className="notifications-popover__heading">
                    {trans('compositions.notificationsPopover.title')}
                </strong>

                {unreadCount > 0 && (
                    <Button
                        type="button"
                        text={trans('compositions.notificationsPopover.markAllRead')}
                        className="notifications-popover__mark-all"
                        onClick={onMarkAllRead}
                    />
                )}
            </div>

            {loading ? (
                <div className="notifications-popover__loading">
                    <LoadingSpinner />
                </div>
            ) : (
                <ul className="notifications-popover__list">
                    {! notifications.length ? (
                        <li className="notifications-popover__empty">
                            {trans('compositions.notificationsPopover.empty')}
                        </li>
                    ) : notifications.map(notification => (
                        <NotificationListItem
                            key={notification.id}
                            notification={notification}
                            onClick={handleNotificationClick}
                        />
                    ))}
                </ul>
            )}
        </Popover>
    )
}
